import type { RoleAssignmentResponse } from '../../../services/api';

/** role_id → Set of selected clinic_ids (clinic scope) */
export type ClinicSelectionMap = Map<string, Set<string>>;

/**
 * Clinic IDs hidden from the picker: Kinnser-synced clinics plus any
 * assignment clinic the actor cannot administer (EMR-783).
 */
export function hiddenClinicIdsForPicker(args: {
  assignments: Array<{ clinic_id?: string | null }>;
  visibleClinicIds?: ReadonlySet<string>;
  preserveClinicIds?: ReadonlySet<string>;
}): Set<string> {
  const hidden = new Set(args.preserveClinicIds ?? []);
  if (!args.visibleClinicIds) return hidden;
  for (const assignment of args.assignments) {
    if (
      assignment.clinic_id != null &&
      !args.visibleClinicIds.has(assignment.clinic_id)
    ) {
      hidden.add(assignment.clinic_id);
    }
  }
  return hidden;
}

/** Build clinic-scoped role selection, omitting hidden clinic IDs. */
export function buildInitialClinicSelectionFromAssignments(
  assignments: RoleAssignmentResponse[],
  hiddenClinicIds?: ReadonlySet<string>
): ClinicSelectionMap {
  const selection: ClinicSelectionMap = new Map();

  for (const assignment of assignments) {
    if (assignment.clinic_id == null) continue;
    if (hiddenClinicIds?.has(assignment.clinic_id)) continue;

    const existing = selection.get(assignment.role_id) ?? new Set<string>();
    existing.add(assignment.clinic_id);
    selection.set(assignment.role_id, existing);
  }

  return selection;
}

export function mergeClinicRoleAssignments(
  visibleAssignments: Array<{ role_id: string; clinic_id: string }>,
  preservedAssignments: Array<{ role_id: string; clinic_id: string }>
): Array<{ role_id: string; clinic_id: string }> {
  const merged = new Map<string, { role_id: string; clinic_id: string }>();
  for (const assignment of [...visibleAssignments, ...preservedAssignments]) {
    merged.set(`${assignment.role_id}:${assignment.clinic_id}`, assignment);
  }
  return [...merged.values()];
}

/**
 * Clinic-role PUT body: picker state minus hidden ids, unioned with Kinnser
 * (or other picker-hidden-but-grantable) assignments so those are not
 * reconciled away. Assignments at clinics the actor cannot administer are
 * omitted — the backend preserves them, matching clinic-access (EMR-783).
 * Posting those clinics would 400 (ROG-2470).
 *
 * A clinic the operator revoked in this dialog overrides every preserve rule
 * for that clinic. Preserving a pair there would outlive the revoke: the role
 * survives in the database, so the next open sees a role granting the clinic,
 * re-checks it in the access list, and the following save hands the access
 * back.
 */
export function clinicRoleAssignmentsForSave(args: {
  clinicSelection: ClinicSelectionMap;
  currentAssignments: Array<{ role_id: string; clinic_id?: string | null }>;
  hiddenClinicIds: ReadonlySet<string>;
  preserveClinicIds?: ReadonlySet<string>;
  /** Grid-hidden clinic roles (e.g. approver). Re-sent so reconcile does not delete them. */
  preserveRoleIds?: ReadonlySet<string>;
  /** Clinics revoked in this dialog: nothing is preserved at them. */
  revokedClinicIds?: ReadonlySet<string>;
}): Array<{ role_id: string; clinic_id: string }> {
  const visible: Array<{ role_id: string; clinic_id: string }> = [];
  for (const [roleId, clinicIds] of args.clinicSelection) {
    for (const clinicId of clinicIds) {
      if (!args.hiddenClinicIds.has(clinicId)) {
        visible.push({ role_id: roleId, clinic_id: clinicId });
      }
    }
  }
  const preserveClinicIds = args.preserveClinicIds ?? new Set<string>();
  const preserveRoleIds = args.preserveRoleIds ?? new Set<string>();
  const revokedClinicIds = args.revokedClinicIds ?? new Set<string>();
  const preserved = args.currentAssignments.flatMap(assignment => {
    if (assignment.clinic_id == null) return [];
    if (revokedClinicIds.has(assignment.clinic_id)) return [];
    const keepKinnser = preserveClinicIds.has(assignment.clinic_id);
    const keepHiddenRoleAtVisibleClinic =
      preserveRoleIds.has(assignment.role_id) &&
      !args.hiddenClinicIds.has(assignment.clinic_id);
    return keepKinnser || keepHiddenRoleAtVisibleClinic
      ? [{ role_id: assignment.role_id, clinic_id: assignment.clinic_id }]
      : [];
  });
  return mergeClinicRoleAssignments(visible, preserved);
}
